import React, { useMemo } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tooltip,
} from '@mui/material';
import { Sweetener, SweetenerService } from 'us-web-services';
import { Table, TableInstance } from 'src/components/common/Table';
import PageStyles from '../../styles/PageStyles';
import DateUtil from '../../util/DateUtil';

function SweetenersLookup(props: RouteComponentProps) {
  const { history } = props;
  const [showConfirmDisable, setShowConfirmDisable] = React.useState(false);
  const [showConfirmEnable, setShowConfirmEnable] = React.useState(false);
  const [currentRow, setCurrentRow] = React.useState<Sweetener>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const tableRef = React.useRef<TableInstance>();

  const { classes } = PageStyles();

  const params = (globalFilter: string) => {
    const paramArray = [];

    if (globalFilter) {
      const encodedFilter = globalFilter.replace("'", "\\'");

      if (!+globalFilter) {
        let q = `label.contains('${encodedFilter}')`;

        q += `.or(type.contains('${encodedFilter}'))`;

        paramArray.push({
          key: 'q',
          value: q,
        });
      } else {
        paramArray.push({
          key: 'id',
          value: globalFilter,
        });
      }
    }

    return paramArray;
  };

  const closeConfirmDisableDialog = () => {
    setShowConfirmDisable(false);
    setCurrentRow(undefined);
  };

  const closeConfirmEnableDialog = () => {
    setShowConfirmEnable(false);
    setCurrentRow(undefined);
  };

  const refreshData = () => tableRef.current?.fetchData();

  const disableItem = async () => {
    if (!currentRow?.id) return;

    setIsSubmitting(true);

    const patchData = {
      id: currentRow.id,
      label: currentRow.label,
      type: currentRow.type,
      disabled: DateUtil.getCurrentDateTimeString(),
    } as Sweetener;

    try {
      await SweetenerService.patch(currentRow.id, patchData);
      closeConfirmDisableDialog();
      setIsSubmitting(false);
      refreshData();
      toast.success('Sweetener disabled successfully');
    } catch {
      setIsSubmitting(false);
      toast.error('Error disabling sweetener');
    }
  };

  const enableItem = async () => {
    if (!currentRow?.id) return;

    setIsSubmitting(true);

    const patchData = {
      id: currentRow.id,
      label: currentRow.label,
      type: currentRow.type,
      disabled: null,
    } as Sweetener;

    try {
      await SweetenerService.patch(currentRow.id, patchData);
      closeConfirmEnableDialog();
      setIsSubmitting(false);
      refreshData();
      toast.success('Sweetener enabled successfully');
    } catch {
      setIsSubmitting(false);
      toast.error('Error enabling sweetener');
    }
  };

  const editItem = async (item) => {
    if (item.values.label === item.row.original.label && item.values.type === item.row.original.type) {
      return;
    }

    const data = {
      id: item.row.original.id,
      label: item.values.label,
      type: item.values.type,
      disabled: item.row.original.disabled,
    } as Sweetener;

    try {
      await SweetenerService.patch(item.row.original.id, data);
      refreshData();
      toast.success('Sweetener updated successfully');
    } catch {
      toast.error('Error updating sweetener');
      refreshData();
    }
  };

  const confirmDisable = (rowData) => {
    setShowConfirmDisable(true);
    setCurrentRow(rowData);
  };

  const confirmEnable = (rowData) => {
    setShowConfirmEnable(true);
    setCurrentRow(rowData);
  };

  const addItem = () => {
    history.push('/sweeteners/add');
  };

  const COLUMNS = useMemo(
    () => [
      {
        id: 'sweeteners',
        header: 'Sweeteners',
        columns: [
          {
            header: 'Status',
            enableEditing: false,
            size: 100,
            accessorFn: row => row?.disabled ? 'Disabled' : 'Enabled',
            Cell: ({ cell }) => (
              <>
                {!!cell?.row?.original.disabled &&
                  <Tooltip title='Enable sweetener' placement='top-start'>
                    <Button
                      size='small'
                      variant='contained'
                      color='error'
                      onClick={() => confirmEnable(cell?.row?.original)}
                    >
                      Disabled
                    </Button>
                  </Tooltip>
                }
                {!cell?.row?.original.disabled &&
                  <Tooltip title='Disable sweetener' placement='top-start'>
                    <Button
                      size='small'
                      variant='contained'
                      color='success'
                      onClick={() => confirmDisable(cell?.row?.original)}
                    >
                      Enabled
                    </Button>
                  </Tooltip>
                }
              </>
            ),
          },
          {
            accessorKey: 'id',
            header: 'ID',
            enableEditing: false,
            size: 60,
          },
          {
            accessorKey: 'type',
            header: 'Type',
          },
          {
            accessorKey: 'label',
            header: 'Label',
          },
        ],
      },
    ],
    [],
  );

  return (
    <Paper className={classes.paper}>
      <Dialog open={showConfirmDisable} onClose={closeConfirmDisableDialog}>
        <DialogTitle>Confirm Sweetener Deactivation</DialogTitle>
        <DialogContent>
          Are you sure you want to disable this sweetener?
          It will no longer appear in the product but will be kept for record purposes.
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDisableDialog} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={disableItem}
            disabled={isSubmitting}
            color='primary'
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showConfirmEnable} onClose={closeConfirmEnableDialog}>
        <DialogTitle>Confirm Sweetener Activation</DialogTitle>
        <DialogContent>
          Are you sure you want to enable this sweetener?
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmEnableDialog} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={enableItem}
            disabled={isSubmitting}
            color='primary'
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Table
        ref={tableRef}
        columns={COLUMNS}
        retrieveData={SweetenerService.getByFilterForAdmin}
        params={params}
        addAction={addItem}
        editAction={editItem}
        enableInlineEditing
        alwaysApplyParams
      />
    </Paper>
  );
}

export default withRouter(SweetenersLookup);
